import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import PerformanceGreen from '@panel/commons/assets/icons/performance-green.svg';
import PerformanceYellow from '@panel/commons/assets/icons/performance-yellow.svg';
import PerformanceRed from '@panel/commons/assets/icons/performance-red.svg';
import PerformanceGrey from '@panel/commons/assets/icons/performance-grey.svg';
import ImpressionsIcon from '@panel/commons/assets/icons/ListIconGray.svg';
import VisitsIcon from '@panel/commons/assets/icons/EyeIconGray.svg';
import LeadsIcon from '@panel/commons/assets/icons/UserIconGray.svg';
import { formatNumber } from '@panel/commons/helpers/formatAmount';

const GeneralData = ({
  performanceStatus,
  leads,
  impressions,
  visits,
  device,
  handleRedirectLeads,
  margin,
  onSharedDesempeno
}) => {
  const { t } = useTranslation();
  return (
    <Container device={device} margin={margin}>
      {performanceStatus && (
        <PerformanceStatus status={performanceStatus} data-panel='container-performance-status'>
          <div data-panel='performance-status'>{t(`panel.aviso.desempeno.status.${performanceStatus}`)}</div>
        </PerformanceStatus>
      )}
      <LeadsData className={`desempeno-leadsData-${device}`} isSharedDesempeno={onSharedDesempeno}>
        <DataWrapper className={`desempeno-leadsData-${device}`}>
          <DataContainer className={`desempeno-leadsData-${device}`} data-panel='container-impressions'>
            <Icon className={`desempeno-leadsData-${device}`} icon={ImpressionsIcon} data-panel='icon-impressions' />
            <InfoText className={`desempeno-leadsData-${device}`} data-panel='leadsData-impressions'>
              <h1 data-panel='impressions'>{t('panel.desempeno.generalData.impressions.title')}</h1>
              <h2 data-panel='impressionsCount'>{formatNumber(impressions) || '0'}</h2>
              <p data-panel='info-impressions'>{t('panel.desempeno.generalData.impressions.text')}</p>
            </InfoText>
          </DataContainer>
        </DataWrapper>
        <DataWrapper className={`desempeno-leadsData-${device}`}>
          <DataContainer className={`desempeno-leadsData-${device}`} data-panel='container-visits'>
            <Icon className={`desempeno-leadsData-${device}`} icon={VisitsIcon} data-panel='icon-visits' />
            <InfoText className={`desempeno-leadsData-${device}`} data-panel='leadsData-visits'>
              <h1 data-panel='visits'>{t('panel.desempeno.generalData.visits.title')}</h1>
              <h2 data-panel='visitsCount'>{formatNumber(visits) || '0'}</h2>
              <p data-panel='info-visits'>{t('panel.desempeno.generalData.visits.text')}</p>
            </InfoText>
          </DataContainer>
        </DataWrapper>
        <DataWrapper className={`desempeno-leadsData-${device}`}>
          <DataContainer className={`desempeno-leadsData-${device}`} data-panel='container-interested'>
            <Icon className={`desempeno-leadsData-${device}`} icon={LeadsIcon} data-panel='icon-interested' />
            <InfoText
              withHover={handleRedirectLeads}
              className={`desempeno-leadsData-${device}`}
              data-panel='leadsData-interested'
            >
              <h1 data-panel='interested'>{t('panel.desempeno.generalData.leads.title')}</h1>
              <h2 data-panel='interestedCount'>
                {formatNumber(leads) || '0'}
                {handleRedirectLeads && (
                  <span onClick={handleRedirectLeads} aria-hidden>
                    Ver consultas
                  </span>
                )}
              </h2>
              <p data-panel='info-interested'>{t('panel.desempeno.generalData.leads.text')}</p>
            </InfoText>
          </DataContainer>
        </DataWrapper>
      </LeadsData>
    </Container>
  );
};

export default GeneralData;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${({ device }) => device !== 'desktop' && '43px'};
  ${({ margin }) => css`
    margin: ${margin};
  `}
`;

const PerformanceColors = {
  ALTO: { background: 'positiveBack', color: 'snackGreen', icon: PerformanceGreen },
  MEDIO: { background: 'lightYellow', color: 'brownYellow', icon: PerformanceYellow },
  BAJO: { background: 'redBack', color: 'negative', icon: PerformanceRed },
  'NO-DATA-AVAILABLE': {
    background: 'lightGray',
    color: 'midLightGray',
    icon: PerformanceGrey
  }
};

const PerformanceStatus = styled.div`
  width: 100%;
  height: 49px;
  border-radius: 8px;
  background-color: ${({ status }) => status && select(PerformanceColors[status].background)};
  display: flex;
  align-items: center;
  justify-content: center;
  > div {
    font-family: Hind;
    font-size: 18px;
    font-weight: 600;
    line-height: 24.3px;
    color: ${({ status }) => status && select(PerformanceColors[status].color)};
    display: flex;
    align-items: center;
    :before {
      content: '';
      background-image: ${({ status }) => status && `url(${PerformanceColors[status].icon})`};
      margin-right: 6px;
      height: 22px;
      width: 22px;
      display: inline-block;
    }
  }
`;

const LeadsData = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: 1px solid ${select('middleGray')};
  border-radius: 8px;
  margin: ${({ isSharedDesempeno }) => (isSharedDesempeno ? '16px 0 8px' : '16px 0 40px')};
  &.desempeno-leadsData-mobile {
    padding: 0 12px;
    height: fit-content;
    flex-direction: column;
  }
`;

const DataWrapper = styled.div`
  position: relative;
  height: 95px;
  margin: 38px 0 23px 0;
  flex: 1;
  display: flex;
  justify-content: center;
  :nth-of-type(2) {
    border-right: 1px solid ${select('middleGray')};
    border-left: 1px solid ${select('middleGray')};
  }
  &.desempeno-leadsData-tablet {
    height: 85px;
    margin: 33px 0 23px 0;
  }
  &.desempeno-leadsData-mobile {
    flex: auto;
    width: 100%;
    margin: 0 12px;
    min-height: 95px;
    justify-content: flex-start;
    align-items: center;
    padding: 14px 0 14px 16px;
    height: fit-content;
    :nth-of-type(2) {
      border-top: 1px solid ${select('middleGray')};
      border-bottom: 1px solid ${select('middleGray')};
      border-left: none;
      border-right: none;
    }
  }
`;

const DataContainer = styled.div`
  position: absolute;
  display: flex;
  bottom: 0;
  &.desempeno-leadsData-mobile {
    position: relative;
    align-items: center;
  }
`;

const Icon = styled.div`
  width: 40px;
  height: 40px;
  background-image: ${({ icon }) => `url(${icon})`};
  background-repeat: no-repeat;
  background-position: center;
  margin-right: 16px;
  &.desempeno-leadsData-mobile {
    width: 48px;
    height: 48px;
    margin-right: 22px;
  }
`;

const InfoText = styled.div`
  h1 {
    font-family: Hind;
    font-size: 18px;
    font-weight: 600;
    line-height: 23px;
  }
  h2 {
    font-family: Hind;
    font-size: 32px;
    font-weight: 700;
    line-height: 51px;
    ${({ withHover }) =>
      withHover &&
      css`
        :hover {
          color: ${getColor('primary')};
          span {
            color: ${getColor('primary')};
          }
        }
      `}
    span {
      text-decoration: underline;
      font-family: Hind;
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
      letter-spacing: 0em;
      text-align: left;
      margin-left: 6px;
      cursor: pointer;
    }
  }
  p {
    font-family: Hind;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: ${select('darkGray')};
    max-width: 155px;
    white-space: pre-wrap;
  }
  &.desempeno-leadsData-tablet {
    h2 {
      font-size: 24px;
      line-height: 31.2px;
    }
  }
  &.desempeno-leadsData-mobile {
    h1 {
      font-size: 18px;
      line-height: 20.8px;
    }
    h2 {
      font-size: 20px;
      line-height: 26px;
    }
    p {
      font-size: 12px;
      line-height: 19.21px;
      max-width: 235px;
    }
  }
`;
